import { useEffect, useState } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Paper,
  Chip,
  IconButton,
  Avatar,
  Alert,
} from '@mui/material'
import {
  Close,
  Person,
  Badge as LicenseIcon,
  Warning,
  CheckCircle,
} from '@mui/icons-material'
import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'
import { useNavigate } from 'react-router-dom'
import { UserIcon } from 'src/v3/core/icons'
import { useAppSelector } from 'src/store/hooks'
import { selectLightweightUsersV4ByService } from 'src/v3/manage/manage.slice'
import axios from 'src/utilities/http-client'
import type { LicenseActivityData } from '../../types'

dayjs.extend(relativeTime)

interface LicenseActivityModalProps {
  event: LicenseActivityData | null
  open: boolean
  onClose: () => void
  linkedServiceID: string
}

export default function LicenseActivityModal({
  event,
  open,
  onClose,
  linkedServiceID,
}: LicenseActivityModalProps) {
  let navigate = useNavigate()
  let [profileImage, setProfileImage] = useState<string>('')

  // Get lightweight users to find basic user info
  let lightweightUsers = useAppSelector(
    selectLightweightUsersV4ByService(linkedServiceID),
  )
  let lightweightUser = lightweightUsers.find((u) => u.id === event?.userID)

  // Fetch profile image
  useEffect(() => {
    if (open && event?.userID) {
      axios
        .get(`/api/v2/manage/profile-image?userID=${event.userID}`)
        .then(({ data }) => {
          setProfileImage(data || '')
          return data
        })
        .catch(() => {
          setProfileImage('')
        })
    }
  }, [open, event?.userID, lightweightUser])

  if (!event) {
    return null
  }

  let hasUserInfo = lightweightUser || event

  // Get action type styling
  let getActionTypeColor = () => {
    switch (event.actionType) {
      case 'expired':
        return 'error'
      case 'expiring-soon':
        return 'warning'
      case 'added':
        return 'success'
      default:
        return 'default'
    }
  }

  let getActionTypeIcon = () => {
    switch (event.actionType) {
      case 'expired':
        return <Warning />
      case 'expiring-soon':
        return <Warning />
      case 'added':
        return <CheckCircle />
      default:
        return <LicenseIcon />
    }
  }

  let getActionTypeLabel = () => {
    switch (event.actionType) {
      case 'expired':
        return 'License Expired'
      case 'expiring-soon':
        return `Expiring in ${event.daysToExpiration} day${event.daysToExpiration === 1 ? '' : 's'}`
      case 'added':
        return 'License Added'
      default:
        return event.actionType
    }
  }

  // Format event data for display
  let displayData = [
    { key: 'License ID', value: event.licenseID, type: 'text' },
    { key: 'License Number', value: event.licenseNumber, type: 'text' },
    { key: 'License State', value: event.licenseState, type: 'text' },
    { key: 'Action Date', value: event.actionDate, type: 'datetime' },
    { key: 'Expiration Date', value: event.expiration, type: 'date' },
    {
      key: 'Days to Expiration',
      value: event.daysToExpiration,
      type: 'number',
    },
  ].filter((item) => item.value !== undefined && item.value !== null)

  let formatDisplayValue = (value: any, type: string): string => {
    if (!value) return 'N/A'

    switch (type) {
      case 'datetime':
        return dayjs(value).format('MMM D, YYYY [at] h:mm A')
      case 'date':
        return dayjs(value).format('MMM D, YYYY')
      case 'number':
        if (typeof value === 'number') {
          if (value < 0) {
            return `${Math.abs(value)} days expired`
          }
          return `${value} days remaining`
        }
        return String(value)
      default:
        return String(value)
    }
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      slotProps={{
        paper: {
          sx: {
            width: '80vw',
            height: '80vh',
            maxWidth: 'none',
            maxHeight: 'none',
            borderRadius: 2,
            bgcolor: 'background.default',
          },
        },
      }}
    >
      <DialogTitle>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="h6">License Activity</Typography>
            <Chip
              label={getActionTypeLabel()}
              color={getActionTypeColor() as any}
              icon={getActionTypeIcon()}
              sx={{
                fontWeight: 500,
                '&.MuiChip-root': {
                  backgroundColor: (theme) => {
                    let colorKey = getActionTypeColor() as
                      'error' | 'warning' | 'success'
                    return `${theme.palette[colorKey].main}20`
                  },
                  color: (theme) => {
                    let colorKey = getActionTypeColor() as
                      'error' | 'warning' | 'success'
                    return theme.palette[colorKey].main
                  },
                },
              }}
            />
          </Box>
          <IconButton onClick={onClose} size="small">
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent sx={{ p: 2 }}>
        {/* Event Metadata */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
            {dayjs(event.actionDate).format('MMMM D, YYYY [at] h:mm A')} •{' '}
            {dayjs(event.actionDate).fromNow()}
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {event.licenseName} License{' '}
            {event.licenseNumber && `(${event.licenseNumber})`}
          </Typography>
        </Box>

        {/* Show alert for critical states */}
        {event.actionType === 'expired' && (
          <Alert severity="error" sx={{ mb: 3 }}>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              This license has expired
            </Typography>
            <Typography variant="body2">
              Expired {Math.abs(event.daysToExpiration || 0)} days ago. Please
              renew immediately.
            </Typography>
          </Alert>
        )}

        {event.actionType === 'expiring-soon' && (
          <Alert severity="warning" sx={{ mb: 3 }}>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              License expiring soon
            </Typography>
            <Typography variant="body2">
              This license expires in {event.daysToExpiration} day
              {event.daysToExpiration === 1 ? '' : 's'}. Please renew before
              expiration.
            </Typography>
          </Alert>
        )}

        {/* Main Content Grid */}
        <Box className="grid grid-cols-1 gap-4 mb-4 lg:grid-cols-2">
          {/* User Information Section */}
          <Paper sx={{ p: 2, bgcolor: 'background.paperDark' }}>
            <Typography
              variant="h6"
              sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}
            >
              <UserIcon />
              User Information
            </Typography>

            {hasUserInfo ? (
              <>
                <Box
                  sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}
                >
                  {profileImage ? (
                    <Avatar
                      sx={{
                        width: 64,
                        height: 64,
                      }}
                      src={profileImage}
                    />
                  ) : (
                    <Avatar
                      sx={{
                        width: 64,
                        height: 64,
                        bgcolor: 'primary.main',
                        fontSize: '1.5rem',
                        fontWeight: 600,
                      }}
                    >
                      {(
                        lightweightUser?.firstName?.[0] ||
                        event.userName?.[0] ||
                        '?'
                      ).toUpperCase()}
                    </Avatar>
                  )}
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
                      {lightweightUser?.firstName && lightweightUser?.lastName
                        ? `${lightweightUser.firstName} ${lightweightUser.lastName}`
                        : event.userName}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {lightweightUser?.primaryEmail || event.userEmail}
                    </Typography>
                  </Box>
                </Box>

                {lightweightUser?.licenseTypes &&
                  lightweightUser.licenseTypes.length > 0 && (
                    <Box sx={{ mb: 2 }}>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: 'block', mb: 1 }}
                      >
                        All License Types
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        {lightweightUser.licenseTypes.map((licenseType) => (
                          <Chip
                            key={licenseType}
                            label={licenseType}
                            size="small"
                            variant="outlined"
                            color={
                              licenseType === event.licenseName
                                ? 'primary'
                                : 'default'
                            }
                          />
                        ))}
                      </Box>
                    </Box>
                  )}

                <Button
                  variant="outlined"
                  startIcon={<Person />}
                  onClick={() => {
                    onClose()
                    navigate(
                      `/manage/roster/${linkedServiceID}/user/${lightweightUser?.id}`,
                    )
                  }}
                  size="small"
                >
                  View User Profile
                </Button>
              </>
            ) : (
              <Box sx={{ py: 2, textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                  User information not available
                </Typography>
              </Box>
            )}
          </Paper>

          {/* License Details Section */}
          <Paper
            sx={{
              p: 2,
              bgcolor: 'background.paperDark',
              display: 'flex',
              flexDirection: 'column',
              height: '100%',
            }}
          >
            <Typography
              variant="h6"
              sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}
            >
              <LicenseIcon />
              License Details
            </Typography>

            <Box className="grid grid-cols-1 gap-4 mb-4">
              <Box>
                <Typography variant="body2" color="text.secondary">
                  License Type
                </Typography>
                <Typography
                  color="primary.main"
                  variant="h4"
                  sx={{
                    fontWeight: 700,
                  }}
                >
                  {event.licenseName}
                </Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Issuing State
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {event.licenseState}
                </Typography>
              </Box>
            </Box>

            {event.expiration && (
              <Box sx={{ mb: 2 }}>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 1 }}
                >
                  Expiration Status
                </Typography>
                <Chip
                  label={
                    event.actionType === 'expired'
                      ? 'Expired'
                      : event.actionType === 'expiring-soon'
                        ? 'Expiring Soon'
                        : 'Active'
                  }
                  color={getActionTypeColor() as any}
                  variant="outlined"
                />
              </Box>
            )}
          </Paper>
        </Box>

        {/* License Details Section */}
        <Paper sx={{ p: 2, bgcolor: 'background.paperDark' }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            License Information
          </Typography>

          <Box className="grid grid-cols-1 gap-1 sm:grid-cols-2">
            {displayData.map(({ key, value, type }) => (
              <Box key={key} sx={{ p: 1 }}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: 'block' }}
                >
                  {key}
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {formatDisplayValue(value, type)}
                </Typography>
              </Box>
            ))}
          </Box>
        </Paper>
      </DialogContent>
      <DialogActions sx={{ p: 2, gap: 1 }}>
        <Button onClick={onClose}>Close</Button>
        {lightweightUser && (
          <Button
            startIcon={<Person />}
            variant="outlined"
            onClick={() => {
              onClose()
              navigate(
                `/manage/roster/${linkedServiceID}/user/${lightweightUser.id}`,
              )
            }}
          >
            View Profile
          </Button>
        )}
      </DialogActions>
    </Dialog>
  )
}
